import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Card,
    CardContent,
    Switch,
    Button,
    Alert,
    Chip,
    Tooltip,
} from '@mui/material';
import Grid2 from '@mui/material/Grid2';
import {
    Chat as ChatIcon,
    Extension as ExtensionIcon,
    Save as SaveIcon,
} from '@mui/icons-material';
import PageHeader from '../../components/PageHeader';
import { themeBackgrounds } from '../../theme/theme';
import { toast } from 'react-toastify';
import callSettingsData from '../../data/callSettings.json';
import { useSelector } from 'react-redux';
import { httpService } from '../../services/httpService';
import { API_BASE_URL, REQ_ENC_KEY } from '../../services/api';
import { decryptResponse, encryptResponse, getSessionKey } from '../../services';

interface CallFeature {
    id: string;
    feature: string;
    description: string;
}


const CallSettingsScreen: React.FC = () => {
    const userDetails = useSelector((state: any) => state.auth.userDetails);
    const applicationDetails = useSelector((state: any) => state.applicationDetails);
    const activePlanDetails = userDetails?.activePlanDetails;
    const audioCallFeatures = activePlanDetails?.features?.audio_call || {};
    const videoCallFeatures = activePlanDetails?.features?.video_call || {};
    const [isLoading, setIsLoading] = useState(false);
    const [showSuccessAlert, setShowSuccessAlert] = useState(false);
    const planDetails = userDetails?.plan_details;
    const isPlanExpired = Boolean(
        planDetails?.billing_end_date &&
        new Date(planDetails.billing_end_date).getTime() <= Date.now()
    );

    // Individual state for each feature using their IDs
    // Core features
    const [oneToOneCalls, setOneToOneCalls] = useState(false);
    const [groupCalls, setGroupCalls] = useState(false);
    const [inviteUsers, setInviteUsers] = useState(false);

    // Extension features
    const [sharedScreen, setSharedScreen] = useState(false);
    const [removeParticipants, setRemoveParticipants] = useState(false);
    const [recordCalls, setRecordCalls] = useState(false);
    const [microphoneControl, setMicrophoneControl] = useState(false);
    const [flexibleJoinScheduleCalls, setFlexibleJoinScheduleCalls] = useState(false);
    const [scheduleCallBySlot, setScheduleCallBySlot] = useState(false);
    const [scheduleCalls, setScheduleCalls] = useState(false);

    useEffect(() => {
        fetchCallSettings();
    }, [userDetails, applicationDetails]);

    const fetchCallSettings = async () => {
        setIsLoading(true);
        if (!userDetails?.organization_id || !applicationDetails?.app_id) {
            return;
        }
        try {
            const payload = {
                data: await encryptResponse({
                    organization_id: userDetails.organization_id,
                    app_id: applicationDetails.app_id,
                    user_id: userDetails.user_id,
                }, REQ_ENC_KEY)
            };
            const response = await httpService.post<any>(`${API_BASE_URL}/user/getApplicationCallSettings`, payload);
            if (response.data.status === 200 && response.data.data) {
                const data = response.data.data;
                const decrypted = await decryptResponse<{
                    call_settings: any;
                }>(data, getSessionKey() as string);
                const { call_settings } = decrypted;
                const settings = call_settings || {};
                setOneToOneCalls(settings.is_one_to_one_calls || false);
                setGroupCalls(settings.is_group_calls || false);
                setInviteUsers(settings.is_invite_users || false);
                setSharedScreen(settings.is_shared_screen || false);
                setRemoveParticipants(settings.is_remove_participants || false);
                setRecordCalls(settings.is_record_calls || false);
                setMicrophoneControl(settings.is_microphone_control || false);
                setFlexibleJoinScheduleCalls(settings.is_flexible_join_schedule_calls || false);
                setScheduleCallBySlot(settings.is_schedule_call_by_slot || false);
                setScheduleCalls(settings.is_schedule_calls || false);
            } else {
                // Handle error case
            }
        } catch (error) {
            // Handle network error
        } finally {
            setIsLoading(false);
        }
    };

    // Function to get state value by feature ID
    const getFeatureState = (id: string): boolean => {
        const stateMap: { [key: string]: boolean } = {
            'one-to-one-calls': oneToOneCalls,
            'group-calls': groupCalls,
            'invite-users': inviteUsers,
            'shared-screen': sharedScreen,
            'remove-participants': removeParticipants,
            'record-calls': recordCalls,
            'microphone-control': microphoneControl,
            'flexible-join-schedule-calls': flexibleJoinScheduleCalls,
            'schedule-call-by-slot': scheduleCallBySlot,
            'schedule-calls': scheduleCalls,
        };
        return stateMap[id] || false;
    };

    // Function to check if a call feature is enabled in the user's plan
    const isFeatureEnabledInPlan = (id: string): boolean => {
        const featurePlanMap: { [key: string]: { type: 'audio' | 'video', key: string } } = {
            'one-to-one-calls': { type: 'audio', key: 'one_to_one_call' },
            'group-calls': { type: 'audio', key: 'group_call' },
            'invite-users': { type: 'audio', key: 'invite_users' },
            'shared-screen': { type: 'video', key: 'share_screen' },
            'remove-participants': { type: 'audio', key: 'remove_participants' },
            'record-calls': { type: 'audio', key: 'recording_enabled' },
            'microphone-control': { type: 'audio', key: 'microphone_control' },
            'flexible-join-schedule-calls': { type: 'audio', key: 'flexible_join_schedule_calls' },
            'schedule-call-by-slot': { type: 'audio', key: 'schedule_call_by_slot' },
            'schedule-calls': { type: 'audio', key: 'schedule_calls' },
        };
        
        const planFeatureConfig = featurePlanMap[id];
        if (!planFeatureConfig) {
            return false;
        }
        
        const features = planFeatureConfig.type === 'audio' ? audioCallFeatures : videoCallFeatures;
        if (!features) {
            return false;
        }
        
        return features[planFeatureConfig.key] === true;
    };

    // Function to set state value by feature ID
    const setFeatureState = (id: string, value: boolean): void => {
        const setterMap: { [key: string]: (value: boolean) => void } = {
            'one-to-one-calls': setOneToOneCalls,
            'group-calls': setGroupCalls,
            'invite-users': setInviteUsers,
            'shared-screen': setSharedScreen,
            'remove-participants': setRemoveParticipants,
            'record-calls': setRecordCalls,
            'microphone-control': setMicrophoneControl,
            'flexible-join-schedule-calls': setFlexibleJoinScheduleCalls,
            'schedule-call-by-slot': setScheduleCallBySlot,
            'schedule-calls': setScheduleCalls,
        };
        const setter = setterMap[id];
        if (setter) {
            setter(value);
        }
    };

    // Handle feature toggle
    const handleFeatureToggle = (featureId: string) => (
        event: React.ChangeEvent<HTMLInputElement>
    ) => {
        setFeatureState(featureId, event.target.checked);
    };

    // Save Settings
    const handleSaveSettings = async () => {
        setIsLoading(true);
        try {
            // Collect all current feature states
            const updatedFlags = {
                'is_one_to_one_calls': oneToOneCalls,
                'is_group_calls': groupCalls,
                'is_invite_users': inviteUsers,
                'is_shared_screen': sharedScreen,
                'is_remove_participants': removeParticipants,
                'is_record_calls': recordCalls,
                'is_microphone_control': microphoneControl,
                'is_flexible_join_schedule_calls': flexibleJoinScheduleCalls,
                'is_schedule_call_by_slot': scheduleCallBySlot,
                'is_schedule_calls': scheduleCalls,
            };

            const payload = {
                data: await encryptResponse({
                    organization_id: userDetails.organization_id,
                    app_id: applicationDetails.app_id,
                    call_settings: updatedFlags,
                }, REQ_ENC_KEY)
            };
            const response = await httpService.post<any>(`${API_BASE_URL}/user/updateApplicationCallSettings`, payload);
            if (response.data.status !== 200) {
                throw new Error('Failed to update call settings');
            }
            // Console log the updated flags
            console.log('Call Settings Updated:', updatedFlags);

            // Simulate API call
            await fetchCallSettings();

            setShowSuccessAlert(true);
            toast.success('Call settings updated successfully!', { position: 'top-right' });

            // Hide success alert after 3 seconds
            setTimeout(() => {
                setShowSuccessAlert(false);
            }, 3000);
        } catch (error) {
            toast.error('Failed to update call settings', { position: 'top-right' });
        } finally {
            setIsLoading(false);
        }
    };
    return (
        <Box>
            {/* Page Header */}
            <PageHeader
                heading="Call Settings"
                subheading="Configure call features, extensions, and user experience settings"
                variant="gradient"
            >
                <Chip
                    label={isPlanExpired ? 'Status: Plan Expired' : 'Status: Active'}
                    color={isPlanExpired ? 'error' : 'success'}
                    variant="filled"
                    size="small"
                    sx={{
                        backgroundColor: isPlanExpired
                            ? 'rgba(211, 47, 47, 0.9)'
                            : 'rgba(76, 175, 80, 0.9)',
                        color: '#ffffff',
                        fontWeight: 600,
                    }}
                />
            </PageHeader>

            {/* Success Alert */}
            {showSuccessAlert && (
                <Alert severity="success" sx={{ mb: 3 }}>
                    Call settings have been updated successfully!
                </Alert>
            )}

            <Grid2 container spacing={1}>
                {/* Core Section */}
                <Grid2 size={{ xs: 12 }}>
                    <Card>
                        <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                                <ChatIcon sx={{ mr: 1, color: 'primary.main' }} />
                                <Typography variant="h6">Core</Typography>
                            </Box>

                            <Grid2 container spacing={2}>
                                {callSettingsData.core.map((feature: CallFeature, index: number) => {
                                    const isEnabledInPlan = isFeatureEnabledInPlan(feature.id);
                                    const isEnabled = isEnabledInPlan && !isPlanExpired;
                                    const tooltipTitle = isPlanExpired
                                        ? 'Plan Expire'
                                        : !isEnabledInPlan
                                            ? 'This feature is not available in your current plan. Please upgrade to access this feature.'
                                            : '';
                                    return (
                                        <Grid2 size={{ xs: 12 }} key={feature.id}>
                                            <Tooltip
                                                title={tooltipTitle}
                                                arrow
                                                placement="top"
                                            >
                                                <Box
                                                    sx={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        padding: 2,
                                                        border: '1px solid #e0e0e0',
                                                        borderRadius: 2,
                                                        backgroundColor: '#fafafa',
                                                        gap: 2,
                                                        opacity: isEnabled ? 1 : 0.5,
                                                        cursor: isEnabled ? 'default' : 'not-allowed',
                                                    }}
                                                >
                                                    <Box
                                                        sx={{
                                                            width: 220,
                                                            minWidth: 220,
                                                            display: 'flex',
                                                            alignItems: 'flex-start',
                                                            justifyContent: 'flex-start',
                                                            pr: 1
                                                        }}
                                                    >
                                                        <Typography variant="subtitle2" sx={{ fontWeight: 600, textAlign: 'left' }}>
                                                            {feature.feature}
                                                        </Typography>
                                                    </Box>

                                                    <Box sx={{ flex: 1 }}>
                                                        <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'left' }}>
                                                            {feature.description}
                                                        </Typography>
                                                    </Box>

                                                    <Box sx={{ ml: 'auto' }}>
                                                        <Switch
                                                            checked={getFeatureState(feature.id)}
                                                            onChange={handleFeatureToggle(feature.id)}
                                                            color="primary"
                                                            disabled={!isEnabled}
                                                        />
                                                    </Box>
                                                </Box>
                                            </Tooltip>
                                        </Grid2>
                                    );
                                })}
                            </Grid2>
                        </CardContent>
                    </Card>
                </Grid2>

                {/* Extensions Section */}
                <Grid2 size={{ xs: 12 }}>
                    <Card>
                        <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                                <ExtensionIcon sx={{ mr: 1, color: 'primary.main' }} />
                                <Typography variant="h6">Advanced</Typography>
                            </Box>
                            <Grid2 container spacing={2}>
                                {callSettingsData.extensions.map((feature: CallFeature, index: number) => {
                                    const isEnabledInPlan = isFeatureEnabledInPlan(feature.id);
                                    const isEnabled = isEnabledInPlan && !isPlanExpired;
                                    const tooltipTitle = isPlanExpired
                                        ? 'Plan Expire'
                                        : !isEnabledInPlan
                                            ? 'This feature is not available in your current plan. Please upgrade to access this feature.'
                                            : '';
                                    return (
                                        <Grid2 size={{ xs: 12 }} key={feature.id}>
                                            <Tooltip
                                                title={tooltipTitle}
                                                arrow
                                                placement="top"
                                            >
                                                <Box
                                                    sx={{
                                                        display: 'flex',
                                                        alignItems: 'center',      // keep items vertically centered
                                                        padding: 2,
                                                        border: '1px solid #e0e0e0',
                                                        borderRadius: 2,
                                                        backgroundColor: '#fafafa',
                                                        gap: 2,
                                                        opacity: isEnabled ? 1 : 0.5,
                                                        cursor: isEnabled ? 'default' : 'not-allowed',
                                                    }}
                                                >
                                                    {/* fixed-width feature title (left aligned) */}
                                                    <Box
                                                        sx={{
                                                            width: 220,              // fixed width for the feature label
                                                            minWidth: 220,          // keep it stable across breakpoints
                                                            display: 'flex',
                                                            alignItems: 'flex-start', // make text sit at top if multi-line
                                                            justifyContent: 'flex-start',
                                                            pr: 1
                                                        }}
                                                    >
                                                        <Typography variant="subtitle2" sx={{ fontWeight: 600, textAlign: 'left' }}>
                                                            {feature.feature}
                                                        </Typography>
                                                    </Box>

                                                    {/* description grows and is left-aligned */}
                                                    <Box sx={{ flex: 1 }}>
                                                        <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'left' }}>
                                                            {feature.description}
                                                        </Typography>
                                                    </Box>

                                                    {/* push switch to the far right */}
                                                    <Box sx={{ ml: 'auto' }}>
                                                        <Switch
                                                            checked={getFeatureState(feature.id)}
                                                            onChange={handleFeatureToggle(feature.id)}
                                                            color="primary"
                                                            disabled={!isEnabled}
                                                        />
                                                    </Box>
                                                </Box>
                                            </Tooltip>
                                        </Grid2>
                                    );
                                })}
                            </Grid2>

                            {/* Save Button */}
                            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3, pt: 3, borderTop: '1px solid #e0e0e0' }}>
                                <Tooltip
                                    title={isPlanExpired ? 'Plan Expire' : ''}
                                    arrow
                                    placement="top"
                                >
                                    <span>
                                        <Button
                                            variant="contained"
                                            onClick={handleSaveSettings}
                                            disabled={isLoading || isPlanExpired}
                                            startIcon={<SaveIcon />}
                                            size="large"
                                            sx={{
                                                background: themeBackgrounds.default,
                                                color: '#ffffff',
                                                px: 4,
                                                '&:hover': {
                                                    background: themeBackgrounds.default,
                                                    opacity: 0.9,
                                                },
                                            }}
                                        >
                                            {isLoading ? 'Saving...' : 'Save Settings'}
                                        </Button>
                                    </span>
                                </Tooltip>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid2>
            </Grid2>
        </Box>
    );
};

export default CallSettingsScreen;